import { useCallback, useState } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

interface ImageUploadWidgetProps {
  onImageSelect: (imageDataUrl: string) => void;
  onCancel?: () => void;
  isAnalyzing?: boolean;
}

const ImageUploadWidget = ({ onImageSelect, onCancel, isAnalyzing = false }: ImageUploadWidgetProps) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleImageUpload = useCallback(
    (file: File) => {
      if (!file.type.startsWith("image/")) {
        alert("Please upload an image file");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const imageDataUrl = e.target?.result as string;
        setSelectedImage(imageDataUrl);
      };
      reader.readAsDataURL(file);
    },
    []
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleImageUpload(files[0]);
      }
    },
    [handleImageUpload]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleImageUpload(files[0]);
      }
    },
    [handleImageUpload]
  );

  const handleAnalyze = () => {
    if (selectedImage) {
      onImageSelect(selectedImage);
    }
  };

  const handleClear = () => {
    setSelectedImage(null);
  };

  return (
    <div className="w-full max-w-3xl mx-auto p-4 sm:p-6">
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
          Upload an Image
        </h2>
        <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
          Take or upload a photo of any product, appliance, tool, software page, business card, flyer, or other business material to create your Genie
        </p>
      </div>

      <Card className="p-6 sm:p-8 shadow-xl">
        {!selectedImage ? (
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`border-3 border-dashed rounded-2xl p-8 sm:p-12 text-center transition-all ${
              isDragging
                ? "border-blue-500 bg-blue-50 scale-105"
                : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
            }`}
          >
            <Upload className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mb-4" />
            <p className="text-base sm:text-lg font-semibold text-gray-800 mb-2">
              Drag and drop your photo here
            </p>
            <p className="text-sm text-gray-500 mb-4 sm:mb-6">or</p>
            <label htmlFor="file-upload">
              <Button
                variant="outline"
                className="cursor-pointer border-2 hover:border-blue-500 hover:bg-blue-50 transition-all rounded-xl px-6 py-3"
                asChild
              >
                <span>
                  <ImageIcon className="mr-2 h-5 w-5" />
                  Browse or Take Photo
                </span>
              </Button>
            </label>
            <input
              id="file-upload"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileInput}
              className="hidden"
            />
            <p className="text-xs text-gray-400 mt-6">
              Supported formats: JPG, PNG, GIF, WebP
            </p>
          </div>
        ) : (
          <div>
            <div className="relative">
              <img
                src={selectedImage}
                alt="Selected"
                className="w-full h-auto rounded-lg max-h-96 object-contain"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="absolute top-2 right-2 bg-white/90 hover:bg-white"
                disabled={isAnalyzing}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleAnalyze}
                disabled={isAnalyzing}
                className="flex-1 h-12 text-white font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105"
                style={{
                  background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
                }}
              >
                {isAnalyzing ? "Analyzing Image..." : "Analyze Image"}
              </Button>
              {onCancel && (
                <Button
                  variant="outline"
                  onClick={onCancel}
                  disabled={isAnalyzing}
                  className="flex-1 h-12 border-2 border-gray-300 hover:bg-gray-50 font-semibold transition-all hover:scale-105"
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        )}
      </Card>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-500">
          Your image is processed securely and never stored permanently
        </p>
      </div>
    </div>
  );
};

export default ImageUploadWidget;
